import Vue from 'vue';
import FloorplansService from '@/services/floorplans';
import Parser from '@/services/adhocParser';

export default {
  state: {
    emailList: [],
    emailModal: false,
    maxFavourites: 10,
    list: [],
    listMode: false,
    favouritesMode: false,
    sorting: false,
    viewingFavoritesList: false,
    activeFPPage: 'FloorplanList',
    isUpdatingAvailability: false
  },
  getters: {
    emailList: state => state.emailList,
    showEmailModal: state => state.emailModal,
    floorplans: state => state.list,
    sorting: state => state.sorting,
    activeFloorplans: (state, getters) => {
      let filteredFloorplans = state.list;
      const activeTypes = getters.activeTypes;
      const activeViews = getters.activeViews;
      if (activeTypes.length > 0) {
        filteredFloorplans = filteredFloorplans
          .filter(floorplan => activeTypes.includes(floorplan.type.slug));
      }
      if (activeViews.length > 0) {
        filteredFloorplans = filteredFloorplans.filter(floorplan =>
          floorplan.stacks.some(stack =>
            stack.view.some(view => activeViews.includes(view))
          )
        );
      }
      // Optional filters. Bath Size Floor
      const filtersSqftRange = getters.filtersSqftRange;
      filteredFloorplans = filteredFloorplans.filter(floorplan =>
        floorplan.interior >= filtersSqftRange.min && floorplan.interior <= filtersSqftRange.max
      );

      const activeBaths = getters.activeBaths;
      if (activeBaths.length > 0) {
        filteredFloorplans = filteredFloorplans
          .filter(floorplan => activeBaths.includes(floorplan.baths.toString()));
      }

      const filtersFloorRange = getters.filtersFloorRange;
      filteredFloorplans = filteredFloorplans.filter(floorplan =>
        floorplan.stacks.some(stack =>
          stack.suites.some(suite => suite.floor >= filtersFloorRange.min && suite.floor <= filtersFloorRange.max)
        )
      );

      filteredFloorplans = filteredFloorplans.filter((plan) => {
        let unitCount = 0;
        unitCount += plan.stacks.reduce((n, stack) =>
          n + stack.suites.filter(suite => suite.status === 'available').length, 0);
        return unitCount > 0;
      });

      return filteredFloorplans;
    },
    activeUnits: (state, getters) =>
      getters.activeFloorplans
        .reduce((n, plan) => { n.push(...plan.stacks); return n; }, [])
        .reduce((n, stack) => { n.push(...stack.suites); return n; }, [])
        .filter(unit => unit.status === 'available')
        .map(unit => unit.name),
    availUnits: (state, getters) => {
      let available = 0;
      const plans = (getters.favouritesMode) ? getters.favourites : getters.activeFloorplans;
      plans.forEach((plan) => {
        const stacks = plan.stacks;
        stacks.forEach((stack) => {
          const units = stack.suites.filter(unit => unit.status === 'available');
          if (units.length > 0) {
            available += units.length;
          }
        });
      });
      return available;
    },
    availUnitsByPlanId: (state, getters) => (planId) => {
      const listToUse = (state.favouritesMode) ? getters.favourites : getters.activeFloorplans;
      const activePlan = listToUse.find(plan => plan.id === planId);
      let available = 0;
      if (activePlan) {
        const stacks = activePlan.stacks;
        stacks.forEach((stack) => {
          const units = stack.suites.filter(unit => unit.status === 'available');
          if (units.length > 0) {
            available += units.length;
          }
        });
      }
      return available;
    },
    favourites: state => state.list.filter(plan => plan.favourited),
    listMode: state => state.listMode,
    maxFavourites: state => state.maxFavourites,
    getFloorplanById: (state, getters) => (planId) => {
      const listToUse = (state.favouritesMode) ? getters.favourites : getters.activeFloorplans;
      const planIndex = listToUse.findIndex(plan => plan.id === planId);
      const currentPlan = listToUse[planIndex];
      return {
        current: currentPlan,
        prev: listToUse[planIndex - 1],
        next: listToUse[planIndex + 1]
      };
    },
    favouritesMode: state => state.favouritesMode,
    viewingFavoritesList: state => state.viewingFavoritesList,
    getActiveFPPage: state => state.activeFPPage,
    getPlanHighestFloor: (state, getters) => (planId) => {
      const plan = getters.floorplans.find(p => p.id === planId);
      let units = plan.stacks.reduce((stack, n) => {
        n.push(...stack.suites);
        return n;
      }, []);
      units = units.filter(unit => unit.status === 'available');
      return Math.max(...units.map(o => o.y));
    },
    isUpdatingAvailability: state => state.isUpdatingAvailability
  },
  actions: {
    async DOWNLOAD_FAVOURITES({ getters }) {
      const plans = getters.favourites.map(plan => plan.images.pdf.url);
      const res = await FloorplansService.downloadFavourites(plans);
      return res.data;
    },
    async EMAIL_FLOORPLANS(_, params) {
      const res = await FloorplansService.emailFloorplans(params);
      return res;
    },
    async FETCH_FLOORPLANS({ commit, rootState }) {
      const projectId = rootState.project.adhocId;
      const awsId = rootState.project.awsId;
      try {
        const floorplans = await FloorplansService.fetchFromADhoc(projectId);
        commit('parseADhocFetchedFloorplans', floorplans);

        if (window.location.href.indexOf('keeley-demo') < 0) {
          const awsPlans = await FloorplansService.fetchFromAWS(awsId);
          commit('parseAwsFetchedFloorplans', awsPlans);
        }

        commit('populateFilters', floorplans.data);
        commit('setAppReady');
      } catch (error) {
        console.error(error);
      }
    },
    SET_EMAIL_LIST({ commit }, list) {
      commit('setEmailList', list);
    },
    SET_SORTING({ commit }, status) {
      commit('setSorting', status);
    },
    SORT_SQFT_ASC({ commit }) {
      commit('sortSqftAsc');
    },
    SORT_SQFT_DESC({ commit }) {
      commit('sortSqftDesc');
    },
    TOGGLE_EMAIL_FLOORPLANS({ commit }, status) {
      commit('toggleEmailFloorplans', status);
    },
    TOGGLE_FAVOURITE_PLAN({ commit, rootState }, planId) {
      const projectId = rootState.project.adhocId;
      commit('toggleFavouritePlan', { planId, projectId });
    },
    TOGGLE_LIST_VIEW({ commit }) {
      commit('togglelistMode');
    },
    TOGGLE_VIEWING_FAVOURITES({ commit }) {
      commit('togglefavouritesMode');
    },
    SET_VIEWING_FAVOURITES({ commit }, onOff) {
      commit('setfavouritesMode', onOff);
    },
    TOGGLE_VIEWING_FAVOURITES_LIST({ commit }, onOff) {
      commit('toggleViewingFavoritesList', onOff);
    },
    SET_ACTIVE_FP_PAGE({ commit }, pageName) {
      commit('setActiveFPPage', pageName);
    },
    UPDATE_UNIT_AVAILABILITY({ commit }, unitUpdate) {
      commit('updateUnitAvailability', unitUpdate);
    },
    SET_UPDATING_AVAILABILITY({ commit }, onOff) {
      commit('setUnitAvailability', onOff);
    }
  },
  mutations: {
    // Initial floor plan data
    parseADhocFetchedFloorplans(state, response) {
      state.list = Parser.parseAdhocFloorplans(response.data);
    },
    // Initial unit availability
    parseAwsFetchedFloorplans(state, response) {
      Parser.parseAwsFloorplans(state.list, response.data);
    },
    setEmailList(state, list) {
      state.emailList.length = 0;
      state.emailList.push(...list);
    },
    setSorting(state, status) {
      state.sorting = status;
    },
    sortSqftAsc(state) {
      state.list.sort((a, b) => a.interior - b.interior);
    },
    sortSqftDesc(state) {
      state.list.sort((a, b) => b.interior - a.interior);
    },
    toggleEmailFloorplans(state, status) {
      state.emailModal = status;
      if (status) {
        document.body.classList.add('y-hidden');
      } else {
        document.body.classList.remove('y-hidden');
      }
    },
    toggleFavouritePlan(state, { planId, projectId }) {
      const matchingPlan = state.list.find(plan => plan.id === planId);
      matchingPlan.favourited = !matchingPlan.favourited;
      if (matchingPlan.favourited) {
        Vue.ma.trackEvent({
          action: 'FavouritedFloorplan',
          properties: {
            project: projectId,
            floorplanName: matchingPlan.name,
            floorplanInterior: matchingPlan.interior,
            floorplanType: matchingPlan.type.slug
          }
        });
      }
    },
    togglelistMode(state) {
      state.listMode = !state.listMode;
    },
    togglefavouritesMode(state) {
      state.favouritesMode = !state.favouritesMode;
    },
    setfavouritesMode(state, onOff) {
      state.favouritesMode = onOff;
    },
    toggleViewingFavoritesList(state, onOff) {
      state.viewingFavoritesList = onOff;
    },
    setActiveFPPage(state, pageName) {
      state.activeFPPage = pageName;
    },
    updateUnitAvailability(state, unitUpdate) {
      // High Rise style
      state.isUpdatingAvailability = true;
      const targetUnit = unitUpdate.units;
      const newStatus = unitUpdate.status;
      targetUnit.forEach((unit) => {
        state.list.forEach((floorplan) => {
          floorplan.stacks.forEach((stack) => {
            stack.suites.forEach((suite) => {
              if (suite.awsUnitId && suite.awsUnitId.toString() === unit.toString()) {
                suite.status = newStatus;
              }
            });
          });
        });
      });

      setTimeout(() => {
        state.isUpdatingAvailability = false;
      }, 500);
    },
    setUnitAvailability(state, onOff) {
      state.isUpdatingAvailability = onOff;
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/Floorplans.js